package org.braunequip.web.components.sections.AdminTables

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.launch
import org.braunequip.web.components.forms.Assets.AdminAssetForm
import org.braunequip.web.components.forms.Maintenance.AddMaintenanceScheduleForm
import org.braunequip.web.components.models.AssetDTO
import org.braunequip.web.components.widgets.AlertManager
import org.braunequip.web.components.widgets.ConfirmModal
import org.braunequip.web.components.widgets.UserAlert
import org.braunequip.web.components.widgets.loadingSpinner
import org.braunequip.web.styles.AdminStyles.*
import org.braunequip.web.util.*
import org.braunequip.web.util.Constants.FONT_FAMILY2
import org.braunequip.web.util.Constants.SHARED.ROWS_PER_REQ
import org.braunequip.web.util.Routes.Admin.FRONT_ASSET_ROUTE
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

data class AdminAssetsTableUiState(
    var showLoading: Boolean = true,
    var updatePopup: Boolean = false,
    var updateAssetsDTO: AssetDTO? = null,

    var eroadPopup: Boolean = false,
    var createPopup: Boolean = false,
    var deletePopup: Boolean = false,
    var schedulePopup: Boolean = false,
)

@Composable
fun AdminAssetsTable() {
    val ctx = rememberPageContext()
    val breakpoint = rememberBreakpoint()
    val scope = rememberCoroutineScope()

    val alertManager = remember { AlertManager() }
    alertManager.DisplayAlerts()

    val assetsList = remember { mutableStateListOf<AssetDTO>() }

    var rowsToSkip by remember { mutableStateOf(0) }
    var showMoreVisibility by remember { mutableStateOf(false) }

    val selectedItems = remember { mutableStateListOf<String>() }
    var selectedText by remember { mutableStateOf("0 Items Selected") }

    var uiState by remember { mutableStateOf(AdminAssetsTableUiState()) }

    /*
        POPUP CONTROL UI STATION
     */
    // Asset
    if (
        uiState.eroadPopup
    ) {
        ConfirmModal(
            actionText = "pull the latest ERoad information",
            subText = "This will overwrite current Asset miles with latest from ERoad, and update ERoad ID if necessary." ,
            onConfirm = {
                uiState = uiState.copy(
                    showLoading = true
                )
                scope.launch {
                    updateAssetsWithERoadData(
                        onSuccess = {
                            alertManager.addAlert(
                                UserAlert(
                                    message = it,
                                    color = Colors.Green,
                                    borderColor = Colors.DarkGreen
                                )
                            )
                        },
                        onError = {
                            alertManager.addAlert(
                                UserAlert(
                                    message = it,
                                    color = Colors.Red,
                                    borderColor = Colors.DarkRed
                                )
                            )
                        }
                    )
                    uiState = uiState.copy(
                        showLoading = false
                    )
                }
                uiState = uiState.copy(
                    eroadPopup = false,
                )
            },
            onDismiss = {
                uiState = uiState.copy(
                    eroadPopup = false,
                )
            }
        )
    }

    if (
        uiState.schedulePopup
    ) {
        if (selectedItems.isNotEmpty()) {
            val selectedAssetPairs = selectedItems.mapNotNull { selectedId ->
                assetsList.find { it.id.toString() == selectedId }?.let { asset ->
                    asset.id?.let { id ->
                        id to asset.assetname
                    }
                }
            }

            AddMaintenanceScheduleForm(
                assetIDList = selectedAssetPairs,
                onSubmit = { newMaintItemRequest ->
                    scope.launch {
                        submitScheduleForItems(
                            request = newMaintItemRequest,
                            onFinish = { text, MaintResponse ->
                                uiState = uiState.copy(
                                    schedulePopup = false
                                )
                                val skippedAssets = MaintResponse
                                alertManager.addAlert(
                                    UserAlert(
                                        message = "Applied Schedule!",
                                        color = Colors.Green,
                                        borderColor = Colors.DarkGreen
                                    )
                                )
                                if (skippedAssets.skippedAssets.isNotEmpty()) {
                                    alertManager.addAlert(
                                        UserAlert(
                                            message = "Schedule Already applied on Asset: ${skippedAssets.skippedAssets.joinToString(", ") { it.id.toString() }}",
                                            color = Colors.Red,
                                            borderColor = Colors.DarkRed,
                                            durationMillis = 10000
                                        )
                                    )
                                }
                            }
                        )

                        rowsToSkip = 0
                        fetchAssets(
                            skip = rowsToSkip,
                            onSuccess = {
                                assetsList.clear()
                                assetsList.addAll(it)
                                rowsToSkip += ROWS_PER_REQ
                                showMoreVisibility = it.size >= ROWS_PER_REQ
                            },
                            onError = {
                                console.log(it)
                            }
                        )
                    }
                },
                onDismiss = {
                    uiState= uiState.copy(
                        schedulePopup = false
                    )
                }
            )
        } else {
            uiState.schedulePopup = false
        }
    }

    if (
        uiState.createPopup
    ) {
        AdminAssetForm(
            toUpdateAsset = uiState.updateAssetsDTO,
            onSubmit = {
                scope.launch {
                    createAsset(it) {}
                    uiState = uiState.copy(
                        createPopup = false,
                        updateAssetsDTO = null
                    )

                    rowsToSkip = 0
                    fetchAssets(
                        skip = rowsToSkip,
                        onSuccess = {
                            assetsList.clear()
                            assetsList.addAll(it)
                            rowsToSkip += ROWS_PER_REQ
                            showMoreVisibility = it.size >= ROWS_PER_REQ
                        },
                        onError = {
                            console.log(it)
                        }
                    )
                }
            },
            onDismiss = {
                uiState = uiState.copy(createPopup = false)
            }
        )
    }

    if (
        uiState.deletePopup
    ) {
        ConfirmModal(
            actionText = "Delete ${selectedItems.size} Asset(s)",
            onConfirm = {
                scope.launch {
                    val result = deleteSelectedAssets(ids = selectedItems)
                    if (result) {
                        rowsToSkip -= selectedItems.size

                        selectedItems.forEach { deletedPostId ->
                            assetsList.removeAll {
                                it.id.toString() == deletedPostId
                            }
                        }

                        uiState = uiState.copy(showLoading = false)
                        selectedItems.clear()
                        selectedText = parseSelectedItemsTextByList(selectedItems)
                    }
                }
                uiState = uiState.copy(
                    deletePopup = false,
                    showLoading = false
                )
            },
            onDismiss = {
                uiState = uiState.copy(
                    deletePopup = false,
                    showLoading = false
                )
            }
        )
    }

    /*
        END OF POPUP SECTION
     */

    LaunchedEffect(Unit) {
        fetchAssets(
            skip = rowsToSkip,
            onSuccess = {
                uiState = uiState.copy(showLoading = false)
                assetsList.clear()
                assetsList.addAll(it)
                rowsToSkip += ROWS_PER_REQ
                showMoreVisibility = it.size >= ROWS_PER_REQ
            },
            onError = {
                console.log(it)
            }
        )
    }

    Row(
        modifier = Modifier
            .padding(6.px)
            .fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            text = selectedText,
            modifier = Modifier
                .fontSize(18.px)
                .fontFamily(FONT_FAMILY2)
                .fontWeight(FontWeight.Light)
                .color(if (selectedItems.size > 0) Colors.Black else Theme.HalfBlack.rgb)
        )

        SpanText(
            text = "${assetsList.size} Total Assets",
            modifier = Modifier
                .fontSize(18.px)
                .fontFamily(FONT_FAMILY2)
                .fontWeight(FontWeight.Light)
        )
    }

    /*
        BUTTON ROW
     */
    Row(
        modifier = Modifier
            .padding(6.px)
            .fillMaxWidth()
            .gap(15.px),
        horizontalArrangement = Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Button(
            attrs = Modifier
                .classNames("button")
                .backgroundColor(Colors.LightGray)
                .color(Colors.White)
                .disabled(selectedItems.isEmpty())
                .onClick {
                    scope.launch {
                        selectedItems.clear()
                        selectedText = "0 Items Selected"
                    }
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaEraser()
            } else {
                Text("Clear")
            }
        }

        Button(
            attrs = Modifier
                .classNames("button", "is-danger")
                .color(Colors.White)
                .disabled(selectedItems.isEmpty())
                .onClick {
                    uiState = uiState.copy(
                        deletePopup = true,
                        showLoading = true
                    )
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaTrash()
            } else {
                Text("Delete")
            }
        }

        Button(
            attrs = Modifier
                .classNames("button")
                .backgroundColor(Theme.Primary.rgb)
                .color(Colors.White)
                .onClick {
                    scope.launch {
                        selectedItems.clear()
                        uiState = uiState.copy(showLoading = true)
                        rowsToSkip = 0

                        fetchAssets(
                            skip = rowsToSkip,
                            onSuccess = {
                                uiState = uiState.copy(showLoading = false)
                                assetsList.clear()
                                assetsList.addAll(it)
                                rowsToSkip = ROWS_PER_REQ
                                showMoreVisibility = it.size >= ROWS_PER_REQ
                            },
                            onError = {
                                console.log(it)
                            }
                        )
                    }
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaRotate()
            } else {
                Text("Refresh")
            }
        }

        Button(
            attrs = Modifier
                .classNames("button")
                .backgroundColor(Theme.Primary.rgb)
                .color(Colors.White)
                .onClick {
                    scope.launch {
                        uiState = uiState.copy(
                            createPopup = true,
                            updateAssetsDTO = null
                        )
                    }
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaPencil()
            } else {
                Text("Create")
            }
        }

        Button(
            attrs = Modifier
                .classNames("button")
                .backgroundColor(if (selectedItems.isEmpty()) Colors.LightGray else Theme.Primary.rgb)
                .color(Colors.White)
                .disabled(selectedItems.isEmpty())
                .onClick {
                    scope.launch {
                        uiState = uiState.copy(
                            schedulePopup = true
                        )
                    }
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaClipboardList()
            } else {
                Text("Schedule Maintenance")
            }
        }

        Button(
            attrs = Modifier
                .classNames("button")
                .backgroundColor(Theme.Primary.rgb)
                .color(Colors.White)
                .onClick {
                    scope.launch {
                        uiState = uiState.copy(
                            eroadPopup = true
                        )
                    }
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaRotate()
            } else {
                Text("Pull ERoad Data")
            }
        }

        if (uiState.showLoading) {
            loadingSpinner()
        }
    }

    ViewAssets(
        assets = assetsList,
        selectedItems = selectedItems,
        onSelect = { id ->
            selectedItems.add(id)
            selectedText = parseSelectedItemsTextByList(selectedItems)
        },
        onDeselect = { id ->
            selectedItems.remove(id)
            selectedText = parseSelectedItemsTextByList(selectedItems)
        },
        onEdit = {
            ctx.router.tryRoutingTo("$FRONT_ASSET_ROUTE/${it.id}")

            //OLD -> For when clicking on row opened update modal.
//            uiState = uiState.copy(
//                updatePopup = true,
//                updateAssetsDTO = it
//            )
        },
        showMoreVisibility = showMoreVisibility,
        onShowMore = {
            scope.launch {
                fetchAssets(
                    skip = rowsToSkip,
                    onSuccess = {
                        if (it.isNotEmpty()) {
                            assetsList.addAll(it)
                            rowsToSkip += ROWS_PER_REQ
                            showMoreVisibility = it.size >= ROWS_PER_REQ
                            if (it.size < ROWS_PER_REQ) {
                                showMoreVisibility = false
                            }
                        }  else {
                            showMoreVisibility = false
                        }
                    },
                    onError = {
                        println(it)
                    }
                )
            }
        },
    )
}

@Composable
fun ViewAssets(
    assets: List<AssetDTO>,
    selectedItems: List<String>,
    onSelect: (String) -> Unit,
    onDeselect: (String) -> Unit,
    onEdit: (AssetDTO) -> Unit,
    showMoreVisibility: Boolean,
    onShowMore: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth() // Ensure it takes full width
            .maxWidth(100.percent) // Constrain width to prevent infinite expansion
            .overflow(Overflow.Auto)
            .border(1.px, LineStyle.Solid, Colors.Grey)
            .backgroundColor(Colors.White),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {

        Box(
            modifier = Modifier
                .overflow(Overflow.Auto) // Enables horizontal scrolling
                .width(100.percent) // Ensures the container does not exceed screen width
        ) {
            Table(
                attrs = Modifier
                    .minWidth(800.px)
                    .fillMaxSize()
                    .classNames("table")
                    .margin(topBottom = 0.px)
                    .padding(topBottom = 0.px)
                    .toAttrs()
            ) {
                Thead(Modifier
                    .toAttrs {
                        style {
                            property("table-layout", "fixed")
                        }
                    }
                ) {
                    Tr {
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(SmallColumnStyle).toAttrs()) {
                            Text("Select")
                        }
//                        Th(
//                            TableHeaderStyle.toModifier()
//                                .then(SmallColumnStyle).toAttrs()) {
//                            Text("ID")
//                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(SmallColumnStyle).toAttrs()) {
                            Text("Type")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(MediumColumnStyle)
                                .toAttrs()
                        ) {
                            Text("Asset Name")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(SmallColumnStyle).toAttrs()) {
                            Text("Total Miles")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(SmallColumnStyle)
                                .toAttrs()
                        ) {
                            Text("Maint-Items")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(MediumColumnStyle)
                                .toAttrs()
                        ) {
                            Text("Equipment Name")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(SmallColumnStyle)
                                .toAttrs()
                        ) {
                            Text("Year")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(MediumColumnStyle)
                                .toAttrs()
                        ) {
                            Text("VIN")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(MediumColumnStyle)
                                .toAttrs()
                        ) {
                            Text("ERoad ID")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(MediumColumnStyle)
                                .toAttrs()
                        ) {
                            Text("Status")
                        }
                        Th(TableHeaderStyle.toModifier()
                            .then(LargeColumnStyle).toAttrs()) {
                            Text("Description")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(MediumColumnStyle).toAttrs()) {
                            Text("Created")
                        }
                    }
                }
                Tbody {
                    assets.forEach { row ->
                        AssetPreview(
                            asset = row,
                            selectedItems = selectedItems,
                            onSelect = onSelect,
                            onDeselect = onDeselect,
                            onEdit = onEdit
                        )
                    }
                }
            }
        }

        SpanText(
            text = "Show More",
            modifier = ShowMoreButtonStyle.toModifier()
                .display(if (showMoreVisibility) DisplayStyle.Block else DisplayStyle.None)
                .onClick {
                    onShowMore()
                }
        )
    }

}

@Composable
fun AssetPreview(
    asset: AssetDTO,
    selectedItems: List<String>,
    onSelect: (String) -> Unit,
    onDeselect: (String) -> Unit,
    onEdit: (AssetDTO) -> Unit,
) {
    // Deriving the checkbox state from the parent-selectedItems
    val checked = selectedItems.contains(asset.id.toString())

    Tr(
        attrs = TableRowHoverStyle.toModifier()
            .onClick {
                onEdit(asset)
            }
            .toAttrs()
    ) {
        Td(SmallColumnStyle.toAttrs()) {
            Box(Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CheckboxInput(
                    checked = checked,
                    attrs = Modifier
                        .onClick {
                            it.stopPropagation()
                            if (checked) {
                                onDeselect(asset.id.toString())
                            } else {
                                onSelect(asset.id.toString())
                            }
                        }
                        .size(20.px)
                        .toAttrs()
                )
            }
        }
//        Td(SmallColumnStyle.toAttrs()) {
//            Text(asset.id.toString())
//        }
        Td(SmallColumnStyle.toAttrs()) {
            asset.type?.let { parseAssetTypes(it).toString() }?.let { Text(it) }
        }
        Td(MediumColumnStyle.toAttrs()) {
            Text(asset.assetname)
        }
        Td(SmallColumnStyle.toAttrs()) {
            Text(asset.miles.pretty())
        }
        Td(SmallColumnStyle.toAttrs()) {
            Text(asset.totalMaintItems?.pretty() ?: "0")
        }
        Td(MediumColumnStyle.toAttrs()) {
            Text(asset.equipmentname)
        }
        Td(SmallColumnStyle.toAttrs()) {
            Text(asset.year)
        }
        Td(MediumColumnStyle.toAttrs()) {
            Text(asset.vin ?: "-")
        }
        Td(MediumColumnStyle.toAttrs()) {
            Text(asset.eroadId ?: "-")
        }
        Td(MediumColumnStyle.toAttrs()) {
            asset.status?.let { parseAssetStatus(it).toString() }?.let { Text(it) }
        }
        Td(LargeColumnStyle.toAttrs{
            style {
                property("display", "-webkit-box")
                property("-webkit-box-orient", "vertical")
                property("-webkit-line-clamp", "3")
                property("overflow", "hidden")
                property("outline", "0px")
            }
        }) {
            if (asset.description.isEmpty() || asset.description.isBlank()) {
                Text("-")
            } else {
                Text(asset.description)
            }
        }
        Td(MediumColumnStyle.toAttrs()) {
            asset.createdAt?.let { formatDate(it) }?.let { Text(it) }
        }
    }
}
